import React, { useEffect, useState } from "react";
import axios from "axios";
import Loading from "../utils/Loading";
import { useAuthContext } from "../hooks/useAuthContext";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import Swal from "sweetalert2";
import { USERS_BASE_URL } from "../services/SignUpService";
import { getRoleDisplay } from "../utils/getRoleDisplay";

const UsersList = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const { user: currentUser } = useAuthContext();
  const [permissionChanges, setPermissionChanges] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");

  // Get all users
  const getAllUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${USERS_BASE_URL}/api/users/list`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentUser.token}`,
        },
      });
      setUsersList(response.data);
      setLoading(false);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to fetch users");
      setLoading(false);
    }
  };

  // Update user permissions
  const updateUserPermissions = async (userId, updates) => {
    try {
      console.log("Updating user permissions:", userId, updates);
      
      const response = await axios.put(
        `${USERS_BASE_URL}/api/users/list/update/${userId}`,
        updates,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${currentUser.token}`,
          },
        }
      );
      
      console.log("Update response:", response.data);
      
      // Update the user in the local state
      setUsersList(prevUsers => 
        prevUsers.map(user => 
          user._id === userId ? { ...user, ...updates } : user
        )
      );
      
      // Show success message
      Swal.fire({
        position: "top-end",
        icon: "success",
        title: "Success",
        text: "User permissions updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
      
      return response.data;
    } catch (error) {
      console.error("Error updating user permissions:", error.response?.data || error.message);
      Swal.fire({
        icon: 'error',
        title: 'Update Failed',
        text: error.response?.data?.message || 'Failed to update user permissions',
      });
      throw error;
    }
  };

  // Toggle admin status
  const toggleAdminStatus = async (userId, currentStatus) => {
    try {
      const result = await Swal.fire({
        title: `${currentStatus ? "Remove" : "Grant"} admin privileges?`,
        text: `Are you sure you want to ${
          currentStatus ? "remove admin privileges from" : "make"
        } this user ${currentStatus ? "" : "an admin"}?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, proceed",
      });

      if (result.isConfirmed) {
        await updateUserPermissions(userId, { isAdmin: !currentStatus });
      }
    } catch (error) {
      console.error("Error toggling admin status:", error);
    }
  };

  // Toggle verification status
  const toggleVerificationStatus = async (userId, currentStatus) => {
    try {
      const result = await Swal.fire({
        title: `${currentStatus ? 'Remove' : 'Grant'} verification status?`,
        text: `Are you sure you want to mark this user as ${currentStatus ? 'unverified' : 'verified'}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, proceed'
      });

      if (result.isConfirmed) {
        // Set loading state
        setLoading(true);
        
        // Update the user verification status
        await updateUserPermissions(userId, { isVerified: !currentStatus });
        
        // Refresh the users list to ensure we have the latest data
        await getAllUsers();
      }
    } catch (error) {
      console.error("Error toggling verification status:", error);
      setLoading(false);
    }
  };

  // Handle role change
  const handleRoleChange = async (userId, role, currentlyHasRole) => {
    try {
      const user = usersList.find(u => u._id === userId);
      if (!user) return;
      
      let updatedRoles;
      if (currentlyHasRole) {
        // Remove role
        updatedRoles = user.role.filter(r => r !== role);
      } else {
        // Add role
        updatedRoles = [...(user.role || []), role];
      }
      
      await updateUserPermissions(userId, { role: updatedRoles });
    } catch (error) {
      console.error("Error updating user role:", error);
    }
  };

  // Delete user
  const deleteUser = async (e, userId) => {
    e.preventDefault();
    try {
      const result = await Swal.fire({
        title: 'Delete User?',
        text: "This action cannot be undone!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete user'
      });
      
      if (result.isConfirmed) {
        await axios.delete(`${USERS_BASE_URL}/api/users/delete/${userId}`, {
          headers: {
            Authorization: `Bearer ${currentUser.token}`,
          },
        });
        
        setUsersList(prevUsers => prevUsers.filter(user => user._id !== userId));
        
        Swal.fire(
          'Deleted!',
          'User has been deleted.',
          'success'
        );
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      Swal.fire({
        icon: 'error',
        title: 'Delete Failed',
        text: error.response?.data?.message || 'Failed to delete user',
      });
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = () => {
    let result = [...usersList];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(user => 
        user.firstName?.toLowerCase().includes(term) || 
        user.lastName?.toLowerCase().includes(term) || 
        user.email?.toLowerCase().includes(term)
      );
    }
    
    // Apply role filter
    if (filterRole !== "all") {
      result = result.filter(user => 
        filterRole === "admin" ? user.isAdmin : 
        user.role?.includes(filterRole)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      if (sortBy === "name") {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (sortBy === "email") {
        comparison = a.email.localeCompare(b.email);
      } else if (sortBy === "date") {
        comparison = new Date(a.createdAt) - new Date(b.createdAt);
      }
      
      return sortOrder === "asc" ? comparison : -comparison;
    });
    
    return result;
  };

  useEffect(() => {
    getAllUsers();
  }, []);

  return (
    <div className="container-fluid py-4">
      {/* Header Card */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card shadow-sm border-0 overflow-hidden">
            <div className="card-header bg-gradient-primary text-white p-4">
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <h2 className="h4 mb-1">
                    <i className="fa fa-users me-2"></i> Users Management
                  </h2>
                  <p className="mb-0 opacity-75">Manage user accounts and permissions</p>
                </div>
                {!loading && !error && usersList.length > 0 && (
                  <div className="d-flex align-items-center">
                    <div className="bg-white bg-opacity-25 rounded-pill px-3 py-2">
                      <span className="fw-bold">{usersList.length}</span>
                      <span className="ms-1 opacity-75">Total Users</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Search and Filter Controls */}
            <div className="card-body border-bottom bg-light p-4">
              <div className="row g-3">
                <div className="col-12 col-md-5">
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa fa-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search users..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="col-12 col-md-7">
                  <div className="d-flex flex-wrap gap-2 justify-content-md-end">
                    <select 
                      className="form-select w-auto" 
                      value={filterRole}
                      onChange={(e) => setFilterRole(e.target.value)}
                    >
                      <option value="all">All Roles</option>
                      <option value="admin">Admins</option>
                      <option value="Developer">Developers</option>
                      <option value="Manager">Managers</option>
                      <option value="Editor">Editors</option>
                      <option value="Viewer">Viewers</option>
                    </select>
                    
                    <select 
                      className="form-select w-auto" 
                      value={`${sortBy}-${sortOrder}`}
                      onChange={(e) => {
                        const [newSortBy, newSortOrder] = e.target.value.split('-');
                        setSortBy(newSortBy);
                        setSortOrder(newSortOrder);
                      }}
                    >
                      <option value="name-asc">Name (A-Z)</option>
                      <option value="name-desc">Name (Z-A)</option>
                      <option value="email-asc">Email (A-Z)</option>
                      <option value="email-desc">Email (Z-A)</option>
                      <option value="date-asc">Oldest First</option>
                      <option value="date-desc">Newest First</option>
                    </select>
                    
                    <button 
                      className="btn btn-primary" 
                      onClick={getAllUsers}
                    >
                      <i className="fa fa-refresh me-1"></i> Refresh
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {loading ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-5 d-flex justify-content-center">
                <Loading />
              </div>
            </div>
          </div>
        </div>
      ) : error ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                <div className="alert alert-danger d-flex align-items-center mb-0" role="alert">
                  <i className="fa fa-exclamation-circle me-3 fs-4"></i>
                  <div>{error}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : filteredAndSortedUsers().length === 0 ? (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body p-5 text-center">
                <div className="py-5">
                  <i className="fa fa-search fa-3x text-muted mb-3"></i>
                  <h3 className="h5 text-muted">No users found</h3>
                  <p className="text-muted">Try adjusting your search or filter criteria</p>
                  <button className="btn btn-outline-primary mt-2" onClick={() => {
                    setSearchTerm("");
                    setFilterRole("all");
                  }}>
                    Clear Filters
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="row">
          <div className="col-12">
            <div className="card shadow-sm border-0 overflow-hidden">
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead>
                    <tr className="bg-light">
                      <th scope="col" className="ps-4 py-3">User</th>
                      <th scope="col" className="py-3">Email</th>
                      <th scope="col" className="py-3">Roles</th>
                      <th scope="col" className="py-3">Status</th>
                      <th scope="col" className="py-3">Joined</th>
                      <th scope="col" className="py-3">Permissions</th>
                      <th scope="col" className="text-end pe-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {filteredAndSortedUsers().map((user) => (
                      <tr key={user._id} className={user._id === currentUser._id ? "bg-light-subtle" : ""}>
                        <td className="ps-4">
                          <div className="d-flex align-items-center">
                            <div className="position-relative">
                              <img
                                src={`https://i.pravatar.cc/50?u=${user._id}`}
                                alt={`${user.firstName} ${user.lastName}`}
                                className="rounded-circle me-3"
                                width="40"
                                height="40"
                                style={{ objectFit: "cover" }}
                              />
                              {user.isAdmin && (
                                <span className="position-absolute bottom-0 start-100 translate-middle badge rounded-pill bg-warning">
                                  <i className="fa fa-star"></i>
                                </span>
                              )}
                            </div>
                            <div>
                              <Link
                                to={`/api/dashboard/user-profile/${user._id}`}
                                className="text-decoration-none"
                              >
                                <h6 className="mb-0 fw-semibold">
                                  {user.firstName} {user.lastName}
                                </h6>
                              </Link>
                              <small className="text-muted">
                                @{user.username || user.email.split("@")[0]}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-envelope-o text-muted me-2"></i>
                            <span>{user.email}</span>
                          </div>
                        </td>
                        <td>
                          {user.role && user.role.length > 0 ? (
                            <div className="d-flex flex-wrap gap-1">
                              {user.role.map((role, index) => (
                                <span
                                  key={index}
                                  className={`badge rounded-pill ${
                                    role === "admin"
                                      ? "bg-primary"
                                      : role === "user"
                                      ? "bg-secondary"
                                      : role === "Developer"
                                      ? "bg-purple"
                                      : role === "Manager"
                                      ? "bg-teal"
                                      : role === "Editor"
                                      ? "bg-indigo"
                                      : "bg-info"
                                  }`}
                                >
                                  {role}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="badge bg-light text-dark border">
                              No roles
                            </span>
                          )}
                        </td>
                        <td>
                          {currentUser.isAdmin && user._id !== currentUser._id ? (
                            <button
                              className={`btn btn-sm border-0 badge rounded-pill py-1 px-2 ${
                                user.isVerified
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                              }`}
                              onClick={() => toggleVerificationStatus(user._id, user.isVerified)}
                              title={`Click to mark as ${user.isVerified ? 'unverified' : 'verified'}`}
                            >
                              <i
                                className={`fa fa-${
                                  user.isVerified ? "check" : "times"
                                }-circle me-1`}
                              ></i>
                              {user.isVerified ? "Verified" : "Unverified"}
                            </button>
                          ) : (
                            <span
                              className={`badge rounded-pill py-1 px-2 ${
                                user.isVerified
                                  ? "bg-success-subtle text-success"
                                  : "bg-danger-subtle text-danger"
                              }`}
                            >
                              <i
                                className={`fa fa-${
                                  user.isVerified ? "check" : "times"
                                }-circle me-1`}
                              ></i>
                              {user.isVerified ? "Verified" : "Unverified"}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="fa fa-calendar-o text-muted me-2"></i>
                            <span>{format(new Date(user.createdAt), "MMM d, yyyy")}</span>
                          </div>
                        </td>
                        <td>
                          {currentUser.isAdmin && !user.isAdmin && (
                            <div className="dropdown">
                              <button
                                className="btn btn-sm btn-outline-primary dropdown-toggle d-flex align-items-center"
                                type="button"
                                id={`permissionsDropdown-${user._id}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                data-bs-auto-close="outside" // Prevents dropdown from closing when clicking inside
                              >
                                <i className="fa fa-shield me-1"></i> Manage Access
                              </button>
                              <div
                                className="dropdown-menu shadow-sm p-0 overflow-hidden"
                                style={{ minWidth: "280px" }}
                                aria-labelledby={`permissionsDropdown-${user._id}`}
                              >
                                {/* Header */}
                                <div className="bg-light p-3 border-bottom">
                                  <h6 className="mb-0 fw-bold">
                                    <i className="fa fa-user-shield me-2"></i> 
                                    User Permissions
                                  </h6>
                                  <small className="text-muted">Manage access for {user.firstName} {user.lastName}</small>
                                </div>
                                
                                {/* Admin Toggle */}
                                <div className="p-3 border-bottom">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                      <h6 className="mb-0 fw-semibold">Admin Access</h6>
                                      <small className="text-muted">Full system control</small>
                                    </div>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`admin-${user._id}`}
                                        checked={user.isAdmin || false}
                                        onChange={() => toggleAdminStatus(user._id, user.isAdmin)}
                                      />
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Verification Toggle */}
                                <div className="p-3 border-bottom">
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                      <h6 className="mb-0 fw-semibold">Account Verification</h6>
                                      <small className="text-muted">Email verified status</small>
                                    </div>
                                    <div className="form-check form-switch">
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`verified-${user._id}`}
                                        checked={user.isVerified || false}
                                        onChange={() => toggleVerificationStatus(user._id, user.isVerified)}
                                      />
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Roles Section */}
                                <div className="p-3">
                                  <h6 className="mb-3 fw-semibold">
                                    <i className="fa fa-tags me-2"></i> User Roles
                                  </h6>
                                  <div className="row g-2">
                                    {["Developer", "Manager", "Editor", "Viewer"].map((role) => (
                                      <div key={role} className="col-6">
                                        <div className="form-check">
                                          <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id={`role-${user._id}-${role}`}
                                            checked={user.role?.includes(role) || false}
                                            onChange={() => handleRoleChange(
                                              user._id,
                                              role,
                                              user.role?.includes(role) || false
                                            )}
                                          />
                                          <label
                                            className="form-check-label"
                                            htmlFor={`role-${user._id}-${role}`}
                                          >
                                            {role}
                                          </label>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                
                                {/* Footer */}
                                <div className="bg-light p-2 border-top d-flex justify-content-end">
                                  <button 
                                    className="btn btn-sm btn-outline-secondary" 
                                    onClick={() => document.getElementById(`permissionsDropdown-${user._id}`).click()}
                                  >
                                    Close
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          {user.isAdmin && user._id !== currentUser._id && (
                            <span className="badge bg-warning text-dark">
                              <i className="fa fa-shield me-1"></i> Admin
                            </span>
                          )}
                          {user._id === currentUser._id && (
                            <span className="badge bg-info text-white">
                              <i className="fa fa-user me-1"></i> You
                            </span>
                          )}
                        </td>
                        <td className="text-end pe-4">
                          <div className="d-flex justify-content-end gap-2">
                            <Link
                              to={`/api/dashboard/user-profile/${user._id}`}
                              className="btn btn-sm btn-outline-primary rounded-circle d-flex align-items-center justify-content-center"
                              style={{ width: "32px", height: "32px" }}
                              title="View Profile"
                            >
                              <i className="fa fa-user"></i>
                            </Link>

                            {currentUser.isAdmin && user._id !== currentUser._id && (
                              <>
                                {/* Admin status toggle */}
                                <button
                                  className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${
                                    user.isAdmin
                                      ? "btn-outline-warning"
                                      : "btn-outline-success"
                                  }`}
                                  style={{ width: "32px", height: "32px" }}
                                  onClick={() => toggleAdminStatus(user._id, user.isAdmin)}
                                  title={user.isAdmin ? "Remove Admin" : "Make Admin"}
                                >
                                  <i className={`fa fa-${user.isAdmin ? "user-times" : "user-plus"}`}></i>
                                </button>
                                
                                {/* Verification status toggle */}
                                <button
                                  className={`btn btn-sm rounded-circle d-flex align-items-center justify-content-center ${
                                    user.isVerified
                                      ? "btn-outline-success"
                                      : "btn-outline-danger"
                                  }`}
                                  style={{ width: "32px", height: "32px" }}
                                  onClick={() => toggleVerificationStatus(user._id, user.isVerified)}
                                  title={user.isVerified ? "Mark as Unverified" : "Verify User"}
                                >
                                  <i className={`fa fa-${user.isVerified ? "check-circle" : "exclamation-circle"}`}></i>
                                </button>
                              </>
                            )}

                            {currentUser.isAdmin && !user.isAdmin && (
                              <button
                                className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center"
                                style={{ width: "32px", height: "32px" }}
                                onClick={(e) => deleteUser(e, user._id)}
                                title="Delete User"
                              >
                                <i className="fa fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Footer */}
              <div className="card-footer bg-white py-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <small className="text-muted">
                      Showing {filteredAndSortedUsers().length} of {usersList.length} users
                    </small>
                  </div>
                  <div>
                    <button className="btn btn-sm btn-outline-primary" onClick={getAllUsers}>
                      <i className="fa fa-refresh me-1"></i> Refresh List
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersList;
